import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicService } from '../services/publicService';
import { areaService } from '../services/areaService';
import { registroLimpiezaService } from '../services/registroLimpiezaService';
import { CrearDetalleTareaLimpieza, CrearRegistroLimpieza } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useTenantStore } from '../store/tenantStore';
import { useAuthStore } from '../store/authStore';
import { empresaService } from '../services/empresaService';
import { formatRut, cleanRut } from '../utils/rut';
import ImageUpload from '../components/ImageUpload';
import ImageGallery from '../components/ImageGallery';
import { ImagenRegistro, imagenRegistroService } from '../services/imagenRegistroService';
import { isApiDebugEnabled } from '../services/api';
import {
  AreaGuardada,
  aAreaGuardada,
  actualizarAreaGuardada,
  agregarPendiente,
  areasPorLimpiar,
  contarPendientes,
  contarRegistrosDeHoy,
  guardarAreas,
  leerArea,
  leerConteoHoy,
  nuevoIdPendiente,
  sincronizarPendientes,
  sumarConteoHoy,
} from '../utils/qrOffline';

type Rol = 'paciente' | 'aseo' | null;

type TareaForm = { tareaId: number; nombre: string; realizada: boolean; motivo?: string; detalleId?: number };

// Registro en curso guardado en el dispositivo: si la pantalla se bloquea o la página
// se recarga, el trabajador retoma el mismo registro en vez de perderlo y dejarlo
// abierto en el servidor. registroId null = iniciado sin señal, el servidor aún no lo conoce.
type RegistroEnCurso = {
  usuarioId: number;
  registroId: number | null;
  fechaHoraInicio: string | null;
  tareaForms: TareaForm[];
  obsAseo: string;
  guardadoEn: number;
};

const REGISTRO_EN_CURSO_TTL_MS = 12 * 60 * 60 * 1000;
const claveRegistroEnCurso = (areaId: number) => `qrRegistroEnCurso:${areaId}`;

const leerRegistroEnCurso = (areaId: number): RegistroEnCurso | null => {
  try {
    const raw = localStorage.getItem(claveRegistroEnCurso(areaId));
    if (!raw) return null;
    const data = JSON.parse(raw) as RegistroEnCurso;
    if (Date.now() - data.guardadoEn > REGISTRO_EN_CURSO_TTL_MS) {
      localStorage.removeItem(claveRegistroEnCurso(areaId));
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const guardarRegistroEnCurso = (areaId: number, data: RegistroEnCurso) => {
  try { localStorage.setItem(claveRegistroEnCurso(areaId), JSON.stringify(data)); } catch {}
};

const borrarRegistroEnCurso = (areaId: number) => {
  try { localStorage.removeItem(claveRegistroEnCurso(areaId)); } catch {}
};

const SIN_CONEXION = 'Sin conexión. Revisa la señal e intenta de nuevo.';

// Mensaje para el trabajador: sin detalles técnicos salvo con ?debugApi=true
const mensajeError = (message: string | undefined, fallback: string, detalleTecnico?: () => string, sinConexion = false) => {
  // Tras un 401 el interceptor ya cerró la sesión y la vista vuelve a pedir el RUT
  if (!useAuthStore.getState().isAuthenticated) return 'Tu sesión venció. Ingresa tu RUT para continuar.';
  if (sinConexion || !navigator.onLine) return SIN_CONEXION;
  const base = message || fallback;
  return isApiDebugEnabled() && detalleTecnico ? `${base}${detalleTecnico()}` : base;
};

const mensajeErrorLogin = (error: any): string | null => {
  if (!error) return null;
  if (!error.response) return SIN_CONEXION;
  return error.response.data?.message || 'No se pudo ingresar. Intenta de nuevo.';
};

const QRPage: React.FC = () => {
  const [params] = useSearchParams();
  const areaId = Number(params.get('areaId'));
  const tenantIdParam = params.get('tenant') ? Number(params.get('tenant')) : undefined;

  // Con sesión iniciada en el celular, es personal de aseo: directo al área
  const [rol, setRol] = useState<Rol>(() => (useAuthStore.getState().isAuthenticated ? 'aseo' : null));

  // Auth para personal de aseo (login por RUT)
  const { isAuthenticated, login, logout, isLoginLoading, loginError } = useAuth();
  const { user } = useAuthStore();
  const { setTenant, tenantId } = useTenantStore();
  const [rut, setRut] = useState('');

  // Estado Paciente
  const [obsPaciente, setObsPaciente] = useState('');
  const [loadingPaciente, setLoadingPaciente] = useState(false);
  const [okPaciente, setOkPaciente] = useState(false);
  const [errorPaciente, setErrorPaciente] = useState<string | null>(null);

  // Estado Aseo
  const [areaNombre, setAreaNombre] = useState<string>('');
  const [tareas, setTareas] = useState<{ id: number; nombre: string; obligatoria: boolean }[]>([]);
  const [loadingAseo, setLoadingAseo] = useState(false);
  const [started, setStarted] = useState(false);
  const [registroId, setRegistroId] = useState<number | null>(null);
  const [fechaHoraInicio, setFechaHoraInicio] = useState<string | null>(null);
  const [errorAseo, setErrorAseo] = useState<string | null>(null);
  const [tenantEstablecido, setTenantEstablecido] = useState(false);
  const [doneAseo, setDoneAseo] = useState(false);
  const [guardadoSinSenal, setGuardadoSinSenal] = useState(false);
  const [obsAseo, setObsAseo] = useState('');
  const [imagenes, setImagenes] = useState<ImagenRegistro[]>([]);
  const [errorImagen, setErrorImagen] = useState<string | null>(null);
  const [pendientes, setPendientes] = useState(0);
  const [resumen, setResumen] = useState<{ hoy: number; porLimpiar: AreaGuardada[] } | null>(null);

  const [tareaForms, setTareaForms] = useState<TareaForm[]>([]);

  const tenantParamValido = tenantIdParam !== undefined && Number.isFinite(tenantIdParam) ? tenantIdParam : null;
  const tenantActual = tenantId ?? tenantParamValido;

  // Establecer tenant ID cuando el usuario se autentica
  useEffect(() => {
    const establecerTenant = async () => {
      if (!isAuthenticated) {
        setTenantEstablecido(false);
        return;
      }

      // SuperAdmin no requiere tenant (bypass en middleware)
      if (user?.rol === 'SuperAdmin') {
        setTenantEstablecido(true);
        setErrorAseo(null);
        return;
      }

      try {
        if (tenantParamValido) {
          const empresas = await empresaService.getMisEmpresas();

          // Si venía tenant en el QR, priorizarlo aunque haya uno en localStorage
          const empresaDesdeQR = empresas.find((empresa) => empresa.id === tenantParamValido);
          if (empresaDesdeQR) {
            setTenant(empresaDesdeQR.id, empresaDesdeQR.nombre);
            setTenantEstablecido(true);
            setErrorAseo(null);
            return;
          }
          // Si el backend luego devuelve 403, el middleware lo frenará; aquí no bloqueamos el QR
          setTenant(tenantParamValido, `Empresa ${tenantParamValido}`);
          setTenantEstablecido(true);
          setErrorAseo(null);
          return;
        }

        const empresas = await empresaService.getMisEmpresas();

        if (!empresas || empresas.length === 0) {
          setErrorAseo('No tienes acceso a ninguna empresa. Contacta al administrador.');
          return;
        }

        // Sin tenant en QR: si ya hay uno en store, úsalo; si no, toma el primero disponible
        if (tenantId) {
          setTenantEstablecido(true);
          setErrorAseo(null);
          return;
        }

        const empresaSeleccionada = empresas[0];

        setTenant(empresaSeleccionada.id, empresaSeleccionada.nombre);
        setTenantEstablecido(true);
        setErrorAseo(null);
      } catch (error: any) {
        // Sin señal: confiar en la empresa del QR o en la última usada; el servidor
        // valida el acceso cuando se envíe el registro
        if (!error?.response && (tenantParamValido || tenantId)) {
          if (tenantParamValido && tenantParamValido !== tenantId) {
            setTenant(tenantParamValido, `Empresa ${tenantParamValido}`);
          }
          setTenantEstablecido(true);
          setErrorAseo(null);
          return;
        }
        setErrorAseo(mensajeError(undefined, 'Error al verificar acceso a empresas. Contacta al administrador.'));
      }
    };

    if (!tenantEstablecido) {
      establecerTenant();
    }
  }, [isAuthenticated, tenantParamValido, tenantId, tenantEstablecido, setTenant, user?.rol]);

  // Guardar en el celular todas las áreas con sus tareas, para poder abrir sin señal
  // los QR de áreas que este celular todavía no ha escaneado
  useEffect(() => {
    if (!isAuthenticated || !tenantEstablecido || !tenantId) return;
    areaService.getAreasActivas()
      .then(res => { if (res.success && res.data) guardarAreas(tenantId, res.data.map(aAreaGuardada)); })
      .catch(() => {});
  }, [isAuthenticated, tenantEstablecido, tenantId]);

  useEffect(() => {
    if (!areaId || !isAuthenticated || rol !== 'aseo' || (!tenantEstablecido && user?.rol !== 'SuperAdmin')) return;
    let cancelado = false;
    // Primero lo guardado (instantáneo y sirve sin señal); después se refresca desde el servidor
    const guardada = tenantId ? leerArea(tenantId, areaId) : null;
    if (guardada) {
      setAreaNombre(guardada.nombre);
      setTareas(guardada.tareas);
    }
    const loadArea = async () => {
      try {
        const areaRes = await areaService.getAreaById(areaId);
        if (cancelado) return;
        if (areaRes.success && areaRes.data) {
          const area = aAreaGuardada(areaRes.data);
          setAreaNombre(area.nombre);
          setTareas(area.tareas);
          if (tenantId) actualizarAreaGuardada(tenantId, area);
        } else if (!guardada) {
          setErrorAseo(areaRes.sinConexion
            ? 'Sin señal, y esta área todavía no está guardada en el celular. Ábrela una vez con señal.'
            : mensajeError(areaRes.message, 'No se pudo cargar el área'));
        }
      } catch (e: any) {
        if (!cancelado && !guardada) setErrorAseo(mensajeError(e?.response?.data?.message, 'Error al cargar el área'));
      }
    };
    loadArea();
    return () => { cancelado = true; };
  }, [areaId, isAuthenticated, rol, tenantEstablecido, tenantId, user?.rol]);

  // Retomar un registro que quedó en curso en este dispositivo
  useEffect(() => {
    if (!areaId || !user?.id || !tenantEstablecido || started) return;
    const enCurso = leerRegistroEnCurso(areaId);
    if (!enCurso || enCurso.usuarioId !== user.id) return;
    setRegistroId(enCurso.registroId);
    setFechaHoraInicio(enCurso.fechaHoraInicio);
    setTareaForms(enCurso.tareaForms);
    setObsAseo(enCurso.obsAseo);
    setStarted(true);
    setRol('aseo');
    if (enCurso.registroId) {
      imagenRegistroService.getByRegistroLimpieza(enCurso.registroId)
        .then(setImagenes)
        .catch(() => {});
    }
  }, [areaId, user?.id, tenantEstablecido, started]);

  useEffect(() => {
    if (!started || !user?.id || doneAseo) return;
    guardarRegistroEnCurso(areaId, { usuarioId: user.id, registroId, fechaHoraInicio, tareaForms, obsAseo, guardadoEn: Date.now() });
  }, [areaId, started, registroId, fechaHoraInicio, tareaForms, obsAseo, user?.id, doneAseo]);

  // Enviar los registros guardados sin señal: al abrir, al volver la conexión y cada minuto
  const sincronizar = useCallback(async () => {
    if (!user?.id || !tenantActual) return;
    await sincronizarPendientes(user.id, tenantActual);
    setPendientes(contarPendientes(user.id));
  }, [user?.id, tenantActual]);

  useEffect(() => {
    if (!isAuthenticated) return;
    sincronizar();
    window.addEventListener('online', sincronizar);
    const intervalo = window.setInterval(sincronizar, 60000);
    return () => {
      window.removeEventListener('online', sincronizar);
      window.clearInterval(intervalo);
    };
  }, [isAuthenticated, sincronizar]);

  const toggleTarea = (tareaId: number) => {
    setTareaForms(prev => prev.map(tf => tf.tareaId === tareaId ? { ...tf, realizada: !tf.realizada } : tf));
  };

  const formatErrorDetails = (errors: unknown): string => {
    if (!errors) return '';
    if (Array.isArray(errors)) {
      return errors.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join('; ');
    }
    if (typeof errors === 'object') {
      return Object.entries(errors as Record<string, unknown>)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return `${key}: ${value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join(', ')}`;
          }
          return `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`;
        })
        .join('; ');
    }
    return String(errors);
  };

  const buildDebugInfo = () => {
    const areaDebug = Number.isFinite(areaId) ? areaId : 'N/D';
    const tenantDebug = typeof tenantId === 'number' ? tenantId : 'sin seleccionar';
    const tenantParamDebug = tenantIdParam === undefined
      ? 'sin parámetro'
      : Number.isFinite(tenantIdParam)
        ? tenantIdParam
        : 'inválido';
    return `\n\nInfo de diagnóstico:\n- Área ID: ${areaDebug}\n- Tenant ID actual: ${tenantDebug}\n- Tenant del QR: ${tenantParamDebug}`;
  };

  const buildErrorMessage = (message: string | undefined, errors: unknown, fallback: string) => {
    return mensajeError(message, fallback, () => {
      const details = formatErrorDetails(errors);
      return `${details ? ` (${details})` : ''}${buildDebugInfo()}`;
    });
  };

  // Acciones Paciente
  const reportarRojo = async () => {
    if (!areaId) { setErrorPaciente('Área no válida'); return; }
    setLoadingPaciente(true);
    setErrorPaciente(null);
    try {
      await publicService.reportarRojo(areaId, obsPaciente, tenantIdParam);
      setOkPaciente(true);
    } catch (e: any) {
      setErrorPaciente(e?.response ? (e.response.data?.message || 'No se pudo enviar el reporte') : SIN_CONEXION);
    } finally {
      setLoadingPaciente(false);
    }
  };

  // Acciones Aseo
  const iniciar = async () => {
    if (!areaId) { setErrorAseo('Área no válida'); return; }
    if (!tenantEstablecido && user?.rol !== 'SuperAdmin') { setErrorAseo('Configurando acceso a empresa...'); return; }
    const ahora = new Date().toISOString();
    // Sin señal el registro vive en el celular y se envía completo al terminar
    const iniciarSinSenal = () => {
      setRegistroId(null);
      setFechaHoraInicio(ahora);
      setStarted(true);
      setTareaForms(tareas.map(t => ({ tareaId: t.id, nombre: t.nombre, realizada: false, motivo: '' })));
    };
    if (!navigator.onLine) { iniciarSinSenal(); return; }
    setLoadingAseo(true);
    setErrorAseo(null);
    try {
      // Crear registro con detalles iniciales (todas las tareas en no realizadas)
      const detallesIniciales: CrearDetalleTareaLimpieza[] = tareas.map(t => ({ tareaId: t.id, realizada: false }));
      const payload: CrearRegistroLimpieza = {
        areaId,
        fechaHoraInicio: ahora,
        observaciones: undefined,
        detallesTareas: detallesIniciales,
      };
      const res = await registroLimpiezaService.createRegistroLimpieza(payload);
      if (res.success && res.data) {
        setRegistroId(res.data.id);
        setFechaHoraInicio(ahora);
        setStarted(true);
        // Construir formularios por tarea con detalleId devuelto por backend
        const mapaDetallePorTarea = new Map<number, number>();
        res.data.detallesTareas.forEach((d: any) => {
          if (typeof d.tareaId === 'number' && typeof d.id === 'number') {
            mapaDetallePorTarea.set(d.tareaId, d.id);
          }
        });
        setTareaForms(tareas.map(t => ({
          tareaId: t.id,
          nombre: t.nombre,
          realizada: false,
          motivo: '',
          detalleId: mapaDetallePorTarea.get(t.id),
        })));
      } else if (res.sinConexion && useAuthStore.getState().isAuthenticated) {
        iniciarSinSenal();
      } else {
        setErrorAseo(buildErrorMessage(res.message, res.errors, 'No se pudo iniciar el registro'));
      }
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message;
      const errors = e?.response?.data?.errors;
      setErrorAseo(buildErrorMessage(message, errors, 'Error al iniciar registro'));
    } finally {
      setLoadingAseo(false);
    }
  };

  const handleImageUploaded = (imagen: ImagenRegistro) => {
    setImagenes(prev => [...prev, imagen]);
    setErrorImagen(null);
  };

  const handleImageDeleted = (imagenId: number) => {
    setImagenes(prev => prev.filter(img => img.id !== imagenId));
  };

  const handleImageError = (message: string) => {
    setErrorImagen(message);
  };

  const cargarResumen = async (usuarioId: number) => {
    setResumen({ hoy: leerConteoHoy(usuarioId), porLimpiar: [] });
    if (!navigator.onLine) return;
    const [hoy, areasRes] = await Promise.all([
      contarRegistrosDeHoy(usuarioId).catch(() => null),
      areaService.getAreasActivas().catch(() => null),
    ]);
    const areas = areasRes?.success && areasRes.data ? areasRes.data.map(aAreaGuardada) : null;
    if (areas && tenantId) guardarAreas(tenantId, areas);
    setResumen(prev => ({
      hoy: hoy ?? prev?.hoy ?? 0,
      porLimpiar: areas ? areasPorLimpiar(areas, areaId) : [],
    }));
  };

  const terminar = (sinSenal: boolean) => {
    borrarRegistroEnCurso(areaId);
    setGuardadoSinSenal(sinSenal);
    setDoneAseo(true);
    if (user?.id) {
      sumarConteoHoy(user.id);
      setPendientes(contarPendientes(user.id));
      cargarResumen(user.id);
    }
  };

  const finalizar = async (forms: TareaForm[] = tareaForms) => {
    if (!started) return;
    if (!tenantEstablecido && user?.rol !== 'SuperAdmin') { setErrorAseo('Configurando acceso a empresa...'); return; }

    // Validar que haya al menos una tarea realizada o una observación a nivel de tarea (motivo)
    const tieneTareaRealizada = forms.some(tf => tf.realizada);
    const tieneObservacionEnTarea = forms.some(tf => !tf.realizada && !!tf.motivo && tf.motivo.trim().length > 0);

    if (!tieneTareaRealizada && !tieneObservacionEnTarea) {
      setErrorAseo('Debe marcar al menos una tarea como realizada o agregar una observación en alguna tarea para finalizar el registro de limpieza');
      return;
    }

    const fin = new Date().toISOString();
    const observaciones = obsAseo || undefined;
    const usuarioId = user?.id;
    const puedeGuardarSinSenal = !!usuarioId && !!tenantActual;

    // Iniciado sin señal: se envía completo cuando vuelva la conexión
    if (registroId === null) {
      if (!puedeGuardarSinSenal || !fechaHoraInicio) { setErrorAseo(SIN_CONEXION); return; }
      agregarPendiente({
        id: nuevoIdPendiente(),
        tipo: 'completo',
        tenantId: tenantActual as number,
        usuarioId: usuarioId as number,
        areaId,
        fechaHoraInicio,
        fechaHoraFin: fin,
        observaciones,
        tareas: forms.map(tf => ({
          tareaId: tf.tareaId,
          realizada: !!tf.realizada,
          motivo: tf.realizada ? undefined : (tf.motivo || undefined),
          fechaHoraRealizacion: tf.realizada ? fin : undefined,
        })),
      });
      terminar(true);
      return;
    }

    setLoadingAseo(true);
    setErrorAseo(null);
    try {
      const detallesUpdate = forms
        .filter(tf => typeof tf.detalleId === 'number')
        .map(tf => ({
          id: tf.detalleId as number,
          realizada: !!tf.realizada,
          motivoNoRealizada: tf.realizada ? undefined : (tf.motivo ? tf.motivo : undefined),
          fechaHoraRealizacion: tf.realizada ? fin : undefined,
        }));
      const res = await registroLimpiezaService.updateRegistroLimpieza(registroId, {
        fechaHoraFin: fin,
        observaciones,
        detallesTareas: detallesUpdate,
      });
      if (res.success) {
        terminar(false);
      } else if (res.sinConexion && puedeGuardarSinSenal && useAuthStore.getState().isAuthenticated) {
        agregarPendiente({
          id: nuevoIdPendiente(),
          tipo: 'finalizar',
          tenantId: tenantActual as number,
          usuarioId: usuarioId as number,
          registroId,
          fechaHoraFin: fin,
          observaciones,
          detalles: detallesUpdate,
        });
        terminar(true);
      } else {
        setErrorAseo(buildErrorMessage(res.message, res.errors, 'No se pudo finalizar la limpieza'));
      }
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message;
      const errors = e?.response?.data?.errors;
      setErrorAseo(buildErrorMessage(message, errors, 'Error al finalizar la limpieza'));
    } finally {
      setLoadingAseo(false);
    }
  };

  // Caso más común: se hizo todo. Un toque en vez de marcar cada tarea y finalizar.
  const finalizarTodoRealizado = () => {
    const todas = tareaForms.map(tf => ({ ...tf, realizada: true }));
    setTareaForms(todas);
    finalizar(todas);
  };

  const ingresar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!rut) return;
    setErrorAseo(null);
    login({ rut });
  };

  const cambiarUsuario = () => {
    setRut('');
    setErrorAseo(null);
    logout();
  };

  const errorLogin = mensajeErrorLogin(loginError);

  // UI
  const Selector = (
    <div className="space-y-4">
      <button onClick={() => setRol('paciente')} className="w-full py-4 text-lg bg-red-600 text-white rounded-md">Paciente o Visitante</button>
      <button onClick={() => setRol('aseo')} className="w-full py-4 text-lg bg-indigo-600 text-white rounded-md">Personal de Aseo</button>
    </div>
  );

  const VistaPaciente = okPaciente ? (
    <div className="text-center">
      <h2 className="text-2xl font-bold mb-4">¡Gracias!</h2>
      <p className="mb-6">Hemos marcado esta área como Roja. El personal de aseo será notificado.</p>
      <button onClick={() => { setRol(null); setOkPaciente(false); setObsPaciente(''); }} className="w-full py-3 bg-gray-900 text-white rounded-md">Finalizar</button>
    </div>
  ) : (
    <>
      {errorPaciente && <div className="mb-4 bg-red-50 text-red-700 p-3 rounded">{errorPaciente}</div>}
      <label className="block text-base mb-1">Comentario (opcional)</label>
      <textarea
        className="w-full border rounded-md p-3 text-base mb-4"
        rows={4}
        placeholder="Ej: Papelera llena, piso mojado, mal olor..."
        value={obsPaciente}
        onChange={(e) => setObsPaciente(e.target.value)}
      />
      <button onClick={reportarRojo} disabled={loadingPaciente} className="w-full py-4 text-lg bg-red-600 text-white rounded-md disabled:opacity-60">
        {loadingPaciente ? 'Enviando...' : 'Área sucia'}
      </button>
      <button onClick={() => setRol(null)} className="w-full py-3 mt-3 text-gray-700 bg-gray-100 rounded-md">Volver</button>
    </>
  );

  const AvisoPendientes = pendientes > 0 && (
    <div className="mb-4 bg-amber-50 text-amber-900 p-3 rounded text-base">
      {pendientes === 1
        ? '1 registro guardado sin señal. Se enviará solo cuando vuelva la conexión.'
        : `${pendientes} registros guardados sin señal. Se enviarán solos cuando vuelva la conexión.`}
    </div>
  );

  const VistaAseo = (
    <>
      {errorAseo && <div className="mb-4 bg-red-50 text-red-700 p-3 rounded">{errorAseo}</div>}
      {isAuthenticated && AvisoPendientes}
      {!isAuthenticated ? (
        <form onSubmit={ingresar}>
          {errorLogin && <div className="mb-4 bg-red-50 text-red-700 p-3 rounded">{errorLogin}</div>}
          <label className="block text-base mb-1">Ingresa tu RUT</label>
          <input
            className="w-full border rounded-md p-3 text-lg mb-4"
            placeholder="Ej: 12.345.678-9"
            value={formatRut(rut)}
            onChange={(e) => setRut(cleanRut(e.target.value))}
            inputMode="text"
          />
          <button type="submit" disabled={isLoginLoading || !rut} className="w-full py-4 text-lg bg-indigo-600 text-white rounded-md disabled:opacity-60">
            {isLoginLoading ? 'Ingresando...' : 'Ingresar'}
          </button>
          <button type="button" onClick={() => setRol(null)} className="w-full py-3 mt-3 text-gray-700 bg-gray-100 rounded-md">Volver</button>
        </form>
      ) : doneAseo ? (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">{guardadoSinSenal ? '¡Guardado en el celular!' : '¡Registro completado!'}</h2>
          <p className="mb-6 text-base text-gray-600">
            {guardadoSinSenal
              ? 'No hay señal. El registro se enviará solo cuando vuelva la conexión.'
              : 'Gracias por mantener el área limpia. El estado del área fue actualizado.'}
          </p>
          {resumen && (
            <div className="mb-6 rounded-lg bg-indigo-50 p-4">
              <div className="text-4xl font-bold text-indigo-700">{resumen.hoy}</div>
              <div className="text-base text-indigo-900">{resumen.hoy === 1 ? 'área registrada hoy' : 'áreas registradas hoy'}</div>
            </div>
          )}
          {resumen && resumen.porLimpiar.length > 0 && (
            <div className="text-left mb-6">
              <h3 className="text-lg font-semibold mb-2">Necesitan limpieza ahora</h3>
              <ul className="space-y-2">
                {resumen.porLimpiar.map(a => (
                  <li key={a.id} className="flex items-center justify-between gap-3 border rounded-lg p-3">
                    <span>
                      <span className="block text-base font-medium">{a.nombre}</span>
                      {a.pisoNombre && <span className="block text-sm text-gray-500">{a.pisoNombre}</span>}
                    </span>
                    <span className={`shrink-0 px-3 py-1 rounded-full text-sm font-medium ${a.estado === 'Rojo' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {a.estado}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-base text-gray-500">Escanea el QR de la siguiente área para registrarla.</p>
        </div>
      ) : !started ? (
        <>
          {user?.nombre && <div className="mb-1 text-base text-gray-600">Hola, {user.nombre}</div>}
          <div className="mb-4 text-xl font-semibold">{areaNombre || 'Área'}</div>
          {/* Sin el área cargada el registro se crearía sin tareas y no se podría finalizar */}
          <button onClick={iniciar} disabled={loadingAseo || !areaNombre} className="w-full py-4 text-lg bg-indigo-600 text-white rounded-md disabled:opacity-60">{loadingAseo ? 'Guardando...' : !areaNombre ? 'Cargando área...' : 'Iniciar registro'}</button>
          <div className="mt-6 space-y-3 text-center">
            <button type="button" onClick={cambiarUsuario} className="block w-full py-2 text-base text-indigo-700 underline">
              {user?.nombre ? `¿No eres ${user.nombre}? Cambiar de usuario` : 'Cambiar de usuario'}
            </button>
            <button type="button" onClick={() => setRol('paciente')} className="block w-full py-2 text-base text-gray-600 underline">
              ¿Eres paciente o visitante? Reportar área sucia
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 bg-green-50 text-green-800 p-3 rounded text-base">
            {registroId === null && 'Sin señal: el registro se guarda en el celular. '}
            Al terminar, toca "Todo realizado" o marca solo las tareas que hiciste.
          </div>
          {tareaForms.length > 0 && (
            <button onClick={finalizarTodoRealizado} disabled={loadingAseo} className="w-full py-5 mb-4 text-xl font-semibold bg-green-600 text-white rounded-lg disabled:opacity-60">
              {loadingAseo ? 'Enviando...' : 'Todo realizado ✓'}
            </button>
          )}
          <div className="space-y-3 mb-6">
            {tareaForms.map(tf => (
              <div key={tf.tareaId} className={`border-2 rounded-lg ${tf.realizada ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <label className="flex items-center justify-between gap-3 p-4 min-h-[60px] cursor-pointer">
                  <span className="text-lg">{tf.nombre}</span>
                  <input type="checkbox" checked={tf.realizada} onChange={() => toggleTarea(tf.tareaId)} className="h-7 w-7 shrink-0 accent-green-600" />
                </label>
                {!tf.realizada && (
                  <div className="px-4 pb-4">
                    <input
                      className="w-full border rounded-md p-3 text-base"
                      placeholder="Motivo si no se hizo (opcional)"
                      value={tf.motivo || ''}
                      onChange={(e) => setTareaForms(prev => prev.map(x => x.tareaId === tf.tareaId ? { ...x, motivo: e.target.value } : x))}
                    />
                  </div>
                )}
              </div>
            ))}
            {tareaForms.length === 0 && <div className="text-base text-gray-500">No hay tareas configuradas para esta área.</div>}
          </div>
          <div className="mb-6">
            <label className="block text-base mb-1">Observaciones (opcional)</label>
            <textarea
              className="w-full border rounded-md p-3 text-base"
              rows={3}
              placeholder="Observaciones generales de la limpieza"
              value={obsAseo}
              onChange={(e) => setObsAseo(e.target.value)}
            />
          </div>

          {/* Subida de imágenes */}
          <div className="mb-6">
            <label className="block text-base mb-3">Imágenes del estado (opcional)</label>
            {registroId === null ? (
              <div className="text-base text-gray-500">Las fotos necesitan señal. Puedes finalizar igual.</div>
            ) : (
              <>
                {errorImagen && (
                  <div className="mb-3 bg-red-50 text-red-700 p-3 rounded text-sm">
                    {errorImagen}
                  </div>
                )}
                <ImageUpload
                  registroLimpiezaId={registroId}
                  onImageUploaded={handleImageUploaded}
                  onError={handleImageError}
                />
                {imagenes.length > 0 && (
                  <div className="mt-4">
                    <ImageGallery
                      imagenes={imagenes}
                      onImageDeleted={handleImageDeleted}
                      onError={handleImageError}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <button onClick={() => finalizar()} disabled={loadingAseo} className="w-full py-4 text-lg bg-green-600 text-white rounded-md disabled:opacity-60">{loadingAseo ? 'Enviando...' : 'Finalizar con lo marcado'}</button>
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-8">
      <div className="w-full max-w-md">
        {!rol ? (
          <>
            <h1 className="text-2xl font-bold text-center mb-6">¿Quién eres?</h1>
            {Selector}
          </>
        ) : rol === 'paciente' ? (
          <>
            <h1 className="text-2xl font-bold text-center mb-1">Reportar limpieza</h1>
            <p className="text-sm text-center text-gray-600 mb-6">Toma menos de 10 segundos</p>
            {VistaPaciente}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-center mb-1">Registro de aseo</h1>
            <p className="text-sm text-center text-gray-600 mb-6">Checklist rápido para registrar</p>
            {VistaAseo}
          </>
        )}
      </div>
    </div>
  );
};

export default QRPage;
